import { fmtNum, fmtCur, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Pencil, ArrowUp, ArrowDown } from "lucide-react";

function KycBadge({ verified, status }) {
  return (
    <Badge
      className={
        verified
          ? "bg-[#326F4B]/12 text-[#326F4B] hover:bg-[#326F4B]/12"
          : "bg-[#D68C3C]/15 text-[#A7773F] hover:bg-[#D68C3C]/15"
      }
      variant="secondary"
    >
      {status || (verified ? "Verified" : "Pending")}
    </Badge>
  );
}

function TypeBadge({ type }) {
  if (!type) return <span className="text-muted-foreground">—</span>;
  const corp = type === "company";
  return (
    <Badge
      className={corp ? "bg-[#5B6ABF]/15 text-[#4A57A3] hover:bg-[#5B6ABF]/15" : "bg-[#2b9bb7]/12 text-[#22798f] hover:bg-[#2b9bb7]/12"}
      variant="secondary"
    >
      {corp ? "Corporate" : "Individual"}
    </Badge>
  );
}

function Field({ label, children, mono }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`truncate text-sm text-foreground ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  );
}

export default function CustomerTable({ rows, loading, selectable = false, selectedIds, onToggle, onToggleAll, onEdit, sortKey, order, onSort, onRowClick }) {
  const allChecked = selectable && rows.length > 0 && rows.every((r) => selectedIds?.has(r.id));
  const cols = [
    { label: "User ID", key: "user_id" },
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Type", key: "type" },
    { label: "Country", key: "country" },
    { label: "Created", key: "created" },
    { label: "KYC", key: "kyc" },
    { label: "Referred By", key: "referred_by" },
    { label: "Deposits", key: "deposits" },
    { label: "Balance", key: "balance" },
  ];
  const colSpan = cols.length + (selectable ? 1 : 0) + (onEdit ? 1 : 0);

  return (
    <div>
      {/* Mobile / small screens: stacked cards */}
      <div className="space-y-3 md:hidden" data-testid="customer-cards">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />
          ))
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border bg-card px-4 py-10 text-center text-muted-foreground" data-testid="customer-cards-empty">
            No customers found.
          </div>
        ) : (
          rows.map((c, i) => {
            const checked = selectable && selectedIds?.has(c.id);
            return (
              <div
                key={c.id || c.user_id || c.email || i}
                onClick={() => onRowClick?.(c)}
                className={`rounded-md border border-border bg-card p-4 ${checked ? "bg-[#EDF3F5]" : ""} ${onRowClick ? "cursor-pointer" : ""}`}
                data-testid={`customer-card-${i}`}
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    {selectable && (
                      <input
                        type="checkbox"
                        checked={!!checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggle?.(c.id)}
                        data-testid={`select-customer-${i}`}
                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#2b9bb7]"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{c.name || "—"}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">{c.user_id || "—"}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <TypeBadge type={c.account_type} />
                    <KycBadge verified={c.kyc_verified} status={c.kyc_status} />
                    {onEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onEdit(c); }}
                        data-testid={`edit-customer-${i}`}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[#2b9bb7]/10 hover:text-[#2b9bb7]"
                        title="Edit customer"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <Field label="Email"><span className="text-muted-foreground">{c.email || "—"}</span></Field>
                  <Field label="Country">{c.country || "—"}</Field>
                  <Field label="Referred By" mono>{c.referred_by || "—"}</Field>
                  <Field label="Created"><span className="text-muted-foreground">{fmtDate(c.created_date)}</span></Field>
                  <Field label="Deposits" mono>{fmtCur(c.deposits)}</Field>
                  <Field label="Balance" mono>{fmtCur(c.balance)}</Field>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop: full table */}
      <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
        <table className="w-full text-sm" data-testid="customer-table">
          <thead>
            <tr className="border-b border-border bg-[#EDF3F5] text-left">
              {selectable && (
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    data-testid="select-all-customers"
                    className="h-4 w-4 cursor-pointer accent-[#2b9bb7]"
                  />
                </th>
              )}
              {cols.map((c, i) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${
                    i >= 8 ? "text-right" : ""
                  }`}
                >
                  {onSort ? (
                    <button
                      onClick={() => onSort(c.key)}
                      data-testid={`sort-header-${c.key}`}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground ${
                        sortKey === c.key ? "text-[#2b9bb7]" : ""
                      }`}
                    >
                      {c.label}
                      {sortKey === c.key && (order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
              {onEdit && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="border-b border-border">
                  <td colSpan={colSpan} className="px-4 py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-[#E6EFF2]" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-muted-foreground" data-testid="customer-table-empty">
                  No customers found.
                </td>
              </tr>
            ) : (
              rows.map((c, i) => {
                const checked = selectable && selectedIds?.has(c.id);
                return (
                  <tr
                    key={c.id || c.user_id || c.email || i}
                    onClick={() => onRowClick?.(c)}
                    className={`border-b border-border transition-colors duration-150 hover:bg-[#E6EFF2] ${checked ? "bg-[#EDF3F5]" : ""} ${onRowClick ? "cursor-pointer" : ""}`}
                    data-testid={`customer-row-${i}`}
                  >
                    {selectable && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!checked}
                          onChange={() => onToggle?.(c.id)}
                          className="h-4 w-4 cursor-pointer accent-[#2b9bb7]"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.user_id || "—"}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{c.name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                    <td className="px-4 py-3"><TypeBadge type={c.account_type} /></td>
                    <td className="px-4 py-3">{c.country || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.created_date)}</td>
                    <td className="px-4 py-3">
                      <KycBadge verified={c.kyc_verified} status={c.kyc_status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{c.referred_by || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(c.deposits)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular">{fmtCur(c.balance)}</td>
                    {onEdit && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(c); }}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[#2b9bb7]/10 hover:text-[#2b9bb7]"
                          title="Edit customer"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
